import {
  ActionIcon,
  Box,
  createStyles,
  Group,
  Stack,
  Text,
  Tooltip,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
  castArray,
  filter,
  flatten,
  includes,
  indexOf,
  isFunction,
  map,
  sortBy,
} from 'lodash/fp';
import React, { useMemo } from 'react';
import { matchPath, useLocation, useNavigate } from 'react-router-dom';

import { useTranslation } from '@portals/i18n';
import { ReactComponent as ChevronDown } from '@portals/icons/linear/chevron-down.svg';
import { ReactComponent as CloudConnection } from '@portals/icons/linear/cloud-connection.svg';
import { ReactComponent as ShopIcon } from '@portals/icons/linear/shop.svg';
import { ReactComponent as CustomersIcon } from '@portals/icons/linear/user-tag.svg';
import { useActiveHub, useIsSidebarOpen } from '@portals/redux';
import {
  CommonConfigurationType,
  DesignType,
  HubType,
  PartnerConfigurationType,
} from '@portals/types';

import {
  HUBS_ROUTES_GROUPS,
  STORE_MANAGEMENT_ROUTES,
} from './hubs-sidebar.constants';
import { HubsSidebarItem } from './HubsSidebarItem';
import { ReactComponent as Connect } from '../../../../assets/img/icons/connect.svg';
import { useAppConfig } from '../../../../context';
import {
  useCommonConfig,
  useCommonDesign,
} from '../../../../hooks/portal-config';
import { useHasSupportSeat } from '../../../../hooks/support-seats';
import { usePermissionAccess } from '../../../permission-access/use-permission-access';

export function HubsSidebarSection<
  TCommonConfigurationType extends CommonConfigurationType
>() {
  const t = useTranslation();
  const isSidebarOpen = useIsSidebarOpen();
  const location = useLocation();
  const navigate = useNavigate();
  const { routes } = useAppConfig();

  const { canView, canEdit, isAdmin } = usePermissionAccess();
  const configuration = useCommonConfig();
  const hasSupportSeat = useHasSupportSeat();
  const design = useCommonDesign();
  const activeHub = useActiveHub();

  const [isOpen, { toggle }] = useDisclosure(true);

  const { cx, classes, theme } = useStyles({ design });

  const filteredRoutes = useMemo(() => {
    // Get all childRoutes out of dashboard routes (they're originally grouped by sections)
    const allRoutes = flatten(
      map(({ id, childRoutes, canAccessRoute }) => {
        const canAccess =
          canAccessRoute?.({
            configuration: configuration.data as TCommonConfigurationType,
            canView,
            canEdit,
            isAdmin,
            hasSupportSeat,
          }) ?? true;

        return canAccess ? childRoutes : [];
      }, routes.dashboard)
    );

    // Filter out routes that are not in the current hub
    const currentHubRoutes = filter(({ id }) => {
      // Store management routes are rendered by themselves in an accordion
      if (activeHub === HubType.Ecommerce && isSidebarOpen) {
        if (STORE_MANAGEMENT_ROUTES.includes(id)) {
          return false;
        }
      }

      return includes(id, HUBS_ROUTES_GROUPS[activeHub]);
    }, allRoutes);

    // Filter out routes that are not accessible
    const accessibleRoutes = filter((childRoute) => {
      const { canAccessRoute, isVisibleInSidebar } = childRoute;

      const canAccess =
        canAccessRoute?.({
          configuration: configuration.data as TCommonConfigurationType,
          canView,
          canEdit,
          isAdmin,
          hasSupportSeat,
        }) ?? true;
      const isVisible =
        isVisibleInSidebar?.({
          configuration: configuration.data as TCommonConfigurationType,
        }) ?? true;

      return canAccess && isVisible;
    }, currentHubRoutes);

    // Sort routes by their position in the HUBS_ROUTES_GROUPS array
    return sortBy(({ id }) => {
      return indexOf(id, HUBS_ROUTES_GROUPS[activeHub]);
    }, accessibleRoutes);
  }, [
    activeHub,
    canEdit,
    canView,
    configuration.data,
    hasSupportSeat,
    isAdmin,
    isSidebarOpen,
    routes.dashboard,
  ]);

  const getIsPathActive = (path: string) => {
    return Boolean(
      matchPath(
        {
          path: path,
          end: true,
        },
        location.pathname
      )
    );
  };

  const canViewStoreManagement =
    (configuration?.data as PartnerConfigurationType)?.pricing_plan?.features
      ?.store === 1 && canView(['store_management', 'finance', 'fulfillment']);

  return (
    <Box className={cx(classes.container, { open: isSidebarOpen })}>
      {filteredRoutes.map((childRoute) => {
        const computedPath = isFunction(childRoute.path)
          ? childRoute.path({
              configuration: configuration.data as TCommonConfigurationType,
              isAdmin,
            })
          : childRoute.path;

        const pathsArray = castArray(computedPath);

        return (
          <Tooltip
            key={childRoute.id}
            sx={{ display: 'block' }}
            label={<Text size="xs">{t(childRoute.id)}</Text>}
            disabled={isSidebarOpen}
            withinPortal
            position="right"
            offset={-5}
          >
            <div>
              <HubsSidebarItem
                indicatorComponent={childRoute.indicatorComponent}
                id={childRoute.id}
                badgeColor={childRoute.badgeColor}
                badgeText={childRoute.badgeText}
                badgeComponent={childRoute.badgeComponent}
                icon={childRoute.icon}
                hasBorder={false}
                to={pathsArray}
                isSidebarOpen={isSidebarOpen}
              />
            </div>
          </Tooltip>
        );
      })}

      {activeHub === HubType.Ecommerce &&
      isSidebarOpen &&
      canViewStoreManagement ? (
        <Box className="sidebar-dropdown-section-wrapper">
          <Group
            className="sidebar-dropdown-section-container"
            px="md"
            pr="xs"
            align="center"
            noWrap
            spacing={0}
            position="apart"
            onClick={toggle}
          >
            <Group spacing={0}>
              <ShopIcon style={{ color: theme.colors.blue_gray[6] }} />

              <Text color="blue_gray.6" size="sm" ml="sm">
                Store Management
              </Text>
            </Group>

            <ActionIcon color="blue_gray.6" variant="transparent">
              <ChevronDown
                style={{
                  transform: `rotate(${isOpen ? 180 : 0}deg)`,
                  transition: 'transform 0.2s ease-in-out',
                }}
              />
            </ActionIcon>
          </Group>

          {isOpen ? (
            <Stack pl="xl" mt="xs" spacing="xs">
              <Stack
                pl="md"
                spacing="xs"
                style={{ borderLeft: `1px solid ${theme.colors.blue_gray[1]}` }}
              >
                {canView(['finance', 'fulfillment']) &&
                (configuration?.data as PartnerConfigurationType)?.pricing_plan
                  ?.features?.store === 1 ? (
                  <Box
                    px="md"
                    py="sm"
                    onClick={() => navigate('/store-management/orders')}
                    className={cx('sidebar-dropdown-item', {
                      active: getIsPathActive('/store-management/orders/*'),
                    })}
                  >
                    <Text pl="xs" size="sm">
                      Orders
                    </Text>
                  </Box>
                ) : null}

                {canView(['store_management', 'finance', 'fulfillment']) &&
                (configuration?.data as PartnerConfigurationType)?.pricing_plan
                  ?.features?.store === 1 ? (
                  <Box
                    px="md"
                    py="sm"
                    onClick={() => navigate('/store-management/subscriptions')}
                    className={cx('sidebar-dropdown-item', {
                      active: getIsPathActive(
                        '/store-management/subscriptions/*'
                      ),
                    })}
                  >
                    <Text pl="xs" size="sm">
                      Subscriptions
                    </Text>
                  </Box>
                ) : null}

                {canView(['store_management', 'finance', 'fulfillment']) &&
                (configuration?.data as PartnerConfigurationType)?.pricing_plan
                  ?.features?.store === 1 ? (
                  <Box
                    px="md"
                    py="sm"
                    onClick={() =>
                      navigate('/store-management/purchased-products')
                    }
                    className={cx('sidebar-dropdown-item', {
                      active: getIsPathActive(
                        '/store-management/purchased-products'
                      ),
                    })}
                  >
                    <Text pl="xs" size="sm">
                      Sold products
                    </Text>
                  </Box>
                ) : null}
              </Stack>
            </Stack>
          ) : null}
        </Box>
      ) : null}

      {activeHub === HubType.Home &&
      (hasSupportSeat || canView(['customers', 'finance', 'fulfillment'])) ? (
        isSidebarOpen ? (
          <Box className="sidebar-dropdown-section-wrapper">
            <Group
              className="sidebar-dropdown-section-container"
              px="md"
              pr="xs"
              align="center"
              noWrap
              spacing={0}
              position="apart"
              onClick={toggle}
            >
              <Group spacing={0}>
                <Connect />

                <Text color="blue_gray.6" size="sm" ml="sm">
                  Connect
                </Text>
              </Group>

              <ActionIcon color="blue_gray.6" variant="transparent">
                <ChevronDown
                  style={{
                    transform: `rotate(${isOpen ? 180 : 0}deg)`,
                    transition: 'transform 0.2s ease-in-out',
                  }}
                />
              </ActionIcon>
            </Group>

            {isOpen ? (
              <Stack pl="xl" mt="xs" spacing="xs">
                <Stack
                  pl="md"
                  spacing="xs"
                  style={{
                    borderLeft: `1px solid ${theme.colors.blue_gray[1]}`,
                  }}
                >
                  {isAdmin ? (
                    <Box
                      px="md"
                      py="sm"
                      onClick={() => navigate('/partners')}
                      className={cx('sidebar-dropdown-item', {
                        active:
                          getIsPathActive('/partners') ||
                          getIsPathActive('/partners/*'),
                      })}
                    >
                      <Text pl="xs" size="sm">
                        Partners
                      </Text>
                    </Box>
                  ) : null}

                  {canView(['customers', 'finance', 'fulfillment']) ||
                  hasSupportSeat ? (
                    <Box
                      px="md"
                      py="sm"
                      onClick={() => navigate('/customers')}
                      className={cx('sidebar-dropdown-item', {
                        active: getIsPathActive('/customers'),
                      })}
                    >
                      <Text pl="xs" size="sm">
                        Customers
                      </Text>
                    </Box>
                  ) : null}
                </Stack>
              </Stack>
            ) : null}
          </Box>
        ) : (
          <Stack align="center" justify="center" spacing="xs">
            {isAdmin ? (
              <ActionIcon
                className={cx(classes.connectItem, {
                  active:
                    getIsPathActive('/partners') ||
                    getIsPathActive('/partners/*'),
                })}
                onClick={() => navigate('/partners')}
              >
                <CustomersIcon />
              </ActionIcon>
            ) : null}

            {canView(['customers', 'finance', 'fulfillment']) ||
            hasSupportSeat ? (
              <ActionIcon
                className={cx(classes.connectItem, {
                  active: getIsPathActive('/customers'),
                })}
                onClick={() => navigate('/customers')}
              >
                <CloudConnection />
              </ActionIcon>
            ) : null}
          </Stack>
        )
      ) : null}
    </Box>
  );
}

const useStyles = createStyles(
  (theme, { design }: { design?: DesignType }) => ({
    container: {
      '.sidebar-header': {
        paddingBlock: theme.spacing.md,
        paddingInline: theme.spacing.xxl,
        letterSpacing: '0.04em',
        fontSize: theme.fontSizes.xs,
        fontWeight: 600,
        lineHeight: 1.33,
        transition: 'opacity 0.15s ease-in-out',
        textTransform: 'none',
        display: 'none',

        '&.settings': {
          position: 'absolute',
          bottom: theme.spacing.xl,
          width: '100%',
        },

        '.sidebar-dropdown-section': {
          backgroundColor: theme.white,

          '&:hover': {
            backgroundColor: theme.colors.gray[1],
          },
        },
      },

      '&.open': {
        '.sidebar-header': {
          display: 'block',
        },
      },

      '&:not(&.open)': {
        '.sidebar-divider': {
          borderBottom: `1px solid ${theme.fn.rgba(
            design?.navigation_menu?.menu_text_color ||
              theme.colors.blue_gray[6],
            0.3
          )}`,
          marginBlock: 28,
          marginLeft: theme.spacing.md,
          width: 44,
        },
      },

      '.sidebar-dropdown-section-wrapper': {
        letterSpacing: '0.04em',
        fontSize: theme.fontSizes.xs,
        fontWeight: 600,
        lineHeight: 1.33,
        transition: 'opacity 0.15s ease-in-out',
        textTransform: 'none',
        paddingInline: theme.spacing.md,
      },

      '.sidebar-dropdown-section-container': {
        width: '100%',
        paddingBlock: theme.spacing.sm,
        borderRadius: theme.radius.md,
        cursor: 'pointer',
        userSelect: 'none',

        '&:hover': {
          backgroundColor: theme.colors.gray[1],
        },
      },

      '.sidebar-dropdown-item': {
        color: theme.colors.blue_gray[6],
        cursor: 'pointer',
        borderRadius: theme.radius.md,
        transition: 'all 0.15s ease-in-out',

        '&:hover': {
          backgroundColor: theme.colors.gray[1],
        },

        '&.active': {
          backgroundColor: theme.colors.blue_accent[4],
          color: theme.white,
        },
      },
    },

    connectItem: {
      width: 44,
      height: 44,
      borderRadius: theme.radius.md,
      color:
        design?.navigation_menu?.menu_text_color || theme.colors.blue_gray[6],

      '&:hover': {
        boxShadow: '0 0 1px rgba(0, 0, 0, 0.04)',
        color:
          design?.navigation_menu?.hovered_section_text_color ||
          theme.colors.blue_gray[6],
        background:
          design?.navigation_menu?.hovered_section_background_color ||
          theme.colors.gray[1],
      },

      '&.active': {
        backgroundColor:
          design?.navigation_menu?.selected_section_background_color ||
          theme.colors.blue_accent[4],
        color:
          design?.navigation_menu?.selected_section_text_color || theme.white,
      },
    },
  })
);
